import React, { useEffect, useMemo, useState } from "react";
import { Loader2, GitCompareArrows, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip,
} from "recharts";
import { toast } from "sonner";
import { ciApi } from "../../components/ci/ciApi";
import { Panel, Kicker, RagBadge, ScoreRing, EmptyNote, DataTable } from "../../components/ci/ciWidgets";

const selectCls =
  "w-full rounded-lg bg-[#060d18] border border-white/15 px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-teal-400/60";

const Delta = ({ a, b }) => {
  if (a == null || b == null) return <Minus className="w-3.5 h-3.5 text-white/30 inline" />;
  const d = a - b;
  if (d === 0) return <span className="text-white/40 text-[12px]">0</span>;
  return (
    <span className={`inline-flex items-center gap-1 text-[12px] font-bold ${d > 0 ? "text-emerald-300" : "text-red-300"}`}>
      {d > 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
      {d > 0 ? `+${d}` : d}
    </span>
  );
};

const CompanyHeader = ({ report, accent, label }) => (
  <Panel className="p-6 flex items-center gap-5">
    <ScoreRing score={report.overall?.score} size={104} label="/ 100" />
    <div className="min-w-0">
      <div className={`text-[10px] tracking-[0.24em] uppercase font-bold mb-1 ${accent}`}>{report.company?.industry}</div>
      <div className="font-serif text-2xl text-white truncate">{label || report.company?.name}</div>
      <div className="flex items-center gap-2 mt-1.5">
        <RagBadge rag={report.overall?.rag} />
        <span className="text-[11px] text-white/45">Evidence {report.overall?.evidence_coverage}%</span>
        {report.overall?.score != null && <span className="text-[10px] text-white/40 uppercase font-bold">Final</span>}
      </div>
      <p className="text-[12px] text-white/55 mt-2 leading-relaxed line-clamp-2">{report.overall?.verdict}</p>
    </div>
  </Panel>
);

export default function CICompare() {
  const [assessments, setAssessments] = useState([]);
  const [idA, setIdA] = useState("");
  const [idB, setIdB] = useState("");
  const [repA, setRepA] = useState(null);
  const [repB, setRepB] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    ciApi.get("/assessments").then((r) => {
      const complete = r.data.assessments.filter((a) => a.status === "complete");
      setAssessments(complete);
      if (complete.length >= 2) {
        setIdA(complete[0].id);
        setIdB(complete[1].id);
      }
    });
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!idA || !idB || idA === idB) return;
      setLoading(true);
      try {
        const [a, b] = await Promise.all([
          ciApi.get(`/assessments/${idA}/report`),
          ciApi.get(`/assessments/${idB}/report`),
        ]);
        setRepA(a.data);
        setRepB(b.data);
      } catch {
        toast.error("Failed to load one of the reports");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [idA, idB]);

  const radarData = useMemo(() => {
    if (!repA || !repB) return [];
    return Object.values(repA.dimensions || {}).map((d) => ({
      dim: d.name.split(" ").slice(0, 2).join(" "),
      fullName: d.name,
      A: d.score ?? 0,
      B: repB.dimensions?.[d.key]?.score ?? 0,
    }));
  }, [repA, repB]);

  const optionLabel = (a) => {
    const base = a.company?.name || "—";
    const dup = assessments.filter((x) => x.company?.name === base).length > 1;
    if (!dup) return base;
    const d = a.created_at;
    const tag = d
      ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
      : String(a.id).slice(0, 6);
    const sameTag = assessments.filter((x) => x.company?.name === base && x.created_at &&
      new Date(x.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) === tag);
    const n = sameTag.findIndex((x) => x.id === a.id);
    return `${base} (${tag}${sameTag.length > 1 && n >= 0 ? ` · #${n + 1}` : ""})`;
  };
  const labelFor = (id, rep) => {
    const a = assessments.find((x) => x.id === id);
    return a ? optionLabel(a) : rep?.company?.name || "—";
  };
  const nameA = labelFor(idA, repA) || "A";
  const nameB = labelFor(idB, repB) || "B";

  return (
    <div data-testid="ci-compare-page">
      <Kicker className="mb-3">Peer Benchmarking · Side by side</Kicker>
      <h1 className="font-serif text-3xl sm:text-4xl mb-8">Assessment compare</h1>

      {assessments.length < 2 ? (
        <Panel className="p-10 text-center">
          <GitCompareArrows className="w-8 h-8 text-teal-300 mx-auto mb-4" />
          <div className="font-serif text-xl mb-2">At least two completed assessments are needed</div>
          <p className="text-[13px] text-white/50">Run another company assessment to unlock side-by-side benchmarking.</p>
        </Panel>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mb-8">
            <div>
              <label className="block text-[11px] tracking-[0.16em] uppercase font-semibold text-teal-300 mb-1.5">Company A</label>
              <select className={selectCls} value={idA} onChange={(e) => setIdA(e.target.value)} data-testid="ci-compare-select-a">
                {assessments.map((a) => (
                  <option key={a.id} value={a.id} disabled={a.id === idB}>
                    {optionLabel(a)} — {a.overall_score ?? "—"}/100
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.16em] uppercase font-semibold text-amber-300 mb-1.5">Company B</label>
              <select className={selectCls} value={idB} onChange={(e) => setIdB(e.target.value)} data-testid="ci-compare-select-b">
                {assessments.map((a) => (
                  <option key={a.id} value={a.id} disabled={a.id === idA}>
                    {optionLabel(a)} — {a.overall_score ?? "—"}/100
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading && (
            <div className="flex justify-center py-16 text-white/40">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}

          {!loading && repA && repB && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <CompanyHeader report={repA} accent="text-teal-300" label={nameA} />
                <CompanyHeader report={repB} accent="text-amber-300" label={nameB} />
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                <Panel className="p-7">
                  <Kicker className="mb-4">Dimension profile overlay</Kicker>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData} outerRadius="70%">
                        <PolarGrid stroke="rgba(255,255,255,0.12)" />
                        <PolarAngleAxis dataKey="dim" tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 10 }} />
                        <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                        <Tooltip
                          contentStyle={{ background: "#0c1626", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontSize: 12 }}
                          labelStyle={{ color: "#fff" }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Radar name={nameA} dataKey="A" stroke="#2dd4bf" fill="#2dd4bf" fillOpacity={0.22} />
                        <Radar name={nameB} dataKey="B" stroke="#fbbf24" fill="#fbbf24" fillOpacity={0.16} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>

                <Panel className="p-7">
                  <Kicker className="mb-4">Dimension scores &amp; delta</Kicker>
                  <DataTable
                    testId="ci-compare-dims-table"
                    headers={["Dimension", nameA, nameB, "Δ (A − B)"]}
                    rows={Object.values(repA.dimensions || {}).map((d) => {
                      const b = repB.dimensions?.[d.key];
                      return [
                        <span className="text-white/85">{d.name}</span>,
                        <span className="font-serif text-lg tabular-nums text-teal-300">{d.score ?? "—"}</span>,
                        <span className="font-serif text-lg tabular-nums text-amber-300">{b?.score ?? "—"}</span>,
                        <Delta a={d.score} b={b?.score} />,
                      ];
                    })}
                  />
                  <div className="mt-4 border-t border-white/10 pt-3 flex items-center gap-6 text-[13px]">
                    <span className="text-white/60">
                      Overall: <b className="text-teal-300 font-serif text-lg">{repA.overall?.score ?? "—"}</b>
                      <span className="text-white/35"> vs </span>
                      <b className="text-amber-300 font-serif text-lg">{repB.overall?.score ?? "—"}</b>
                    </span>
                    <Delta a={repA.overall?.score} b={repB.overall?.score} />
                  </div>
                </Panel>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {[
                  [repA, nameA, "text-teal-300"],
                  [repB, nameB, "text-amber-300"],
                ].map(([rep, nm, cls], panelIdx) => (
                  <Panel key={`${nm}-${panelIdx}`} className="p-6">
                    <div className={`text-[11px] tracking-[0.2em] uppercase font-bold mb-4 ${cls}`}>{nm}</div>
                    <div className="grid grid-cols-2 gap-5">
                      <div>
                        <div className="text-[10px] tracking-[0.16em] uppercase text-emerald-300 font-bold mb-2">Strengths</div>
                        <ul className="space-y-1.5">
                          {(rep.executive_summary?.top_strengths || []).slice(0, 4).map((x, i) => (
                            <li key={i} className="text-[12px] text-white/60 leading-relaxed">· {x}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <div className="text-[10px] tracking-[0.16em] uppercase text-red-300 font-bold mb-2">Risks</div>
                        <ul className="space-y-1.5">
                          {(rep.executive_summary?.top_risks || []).slice(0, 4).map((x, i) => (
                            <li key={i} className="text-[12px] text-white/60 leading-relaxed">· {x}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    {rep.overall?.value_creation_potential && (
                      <div className="mt-4 rounded-lg border border-amber-400/20 bg-amber-400/[0.05] px-3.5 py-2.5 text-[12px] text-amber-100/80">
                        <b>Value potential:</b> {rep.overall.value_creation_potential}
                      </div>
                    )}
                  </Panel>
                ))}
              </div>

              {(!repA.peers?.length ? null : (
                <Panel className="p-6">
                  <Kicker className="mb-3">Context</Kicker>
                  <p className="text-[12px] text-white/50 leading-relaxed">
                    Scores are only directly comparable when companies share similar industry dynamics, scale and reporting
                    quality. {nameA} and {nameB} were each scored against their own approved peer sets — treat cross-company
                    deltas as directional, not absolute.
                  </p>
                </Panel>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
